import { computed, inject } from "@angular/core";
import { NavigationStart, Router, Event } from "@angular/router";
import {
  signalStore,
  withComputed,
  withMethods,
  withState,
  patchState,
} from "@ngrx/signals";
import { AuthService } from "./shared/services";
import { toSignal } from "@angular/core/rxjs-interop";

type AppState = {
  globalLoading: boolean;
  sidenavOpen: boolean;
  sidenavCollapsed: boolean;
  activeNavKey: string | null;
  expandedGroups: Set<string>;
};

const SIDENAV_COLLAPSED_KEY = "foresight_sidenav_collapsed";

function loadSidenavCollapsedState(): boolean {
  try {
    const stored = localStorage.getItem(SIDENAV_COLLAPSED_KEY);
    return stored === "true";
  } catch (error) {
    console.warn(
      "Failed to load sidenav collapsed state from localStorage:",
      error,
    );
    return false;
  }
}

function saveSidenavCollapsedState(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDENAV_COLLAPSED_KEY, String(collapsed));
  } catch (error) {
    console.error(
      "Failed to save sidenav collapsed state to localStorage:",
      error,
    );
  }
}

const initialState: AppState = {
  globalLoading: false,
  sidenavOpen: true,
  sidenavCollapsed: loadSidenavCollapsedState(),
  activeNavKey: null,
  expandedGroups: new Set<string>(),
};

export const AppStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed(
    (_store, authService = inject(AuthService), router = inject(Router)) => {
      const routerEvent = toSignal<Event | null>(router.events, {
        initialValue: null,
      });

      return {
        account: authService.account,
        userProfile: authService.userProfile,
        isAttorneyOnly: authService.isAttorneyOnly,

        isAuthenticated: computed(() => !!authService.account()),

        userName: computed(
          () =>
            authService.userProfile()?.displayName ??
            authService.account()?.name ??
            "",
        ),

        isProfileReady: computed(() => !!authService.userProfile()),

        navigationLoading: computed(() => {
          const event = routerEvent();
          return event instanceof NavigationStart;
        }),
      };
    },
  ),
  withMethods((store, authService = inject(AuthService)) => ({
    login: () => authService.loginRedirect(),
    logout: () => authService.logout(),

    startGlobalLoading: () => patchState(store, { globalLoading: true }),
    stopGlobalLoading: () => patchState(store, { globalLoading: false }),
    setGlobalLoading: (value: boolean) =>
      patchState(store, { globalLoading: value }),

    toggleSidenav: () =>
      patchState(store, { sidenavOpen: !store.sidenavOpen() }),
    setSidenavOpen: (open: boolean) => patchState(store, { sidenavOpen: open }),

    toggleSidenavCollapsed: () => {
      const newCollapsedState = !store.sidenavCollapsed();
      patchState(store, { sidenavCollapsed: newCollapsedState });
      saveSidenavCollapsedState(newCollapsedState);
    },
    setSidenavCollapsed: (collapsed: boolean) => {
      patchState(store, { sidenavCollapsed: collapsed });
      saveSidenavCollapsedState(collapsed);
    },

    setActiveNav: (key: string) => patchState(store, { activeNavKey: key }),

    toggleNavGroup: (key: string) => {
      const newGroups = new Set(store.expandedGroups());
      if (newGroups.has(key)) {
        newGroups.delete(key);
      } else {
        newGroups.clear();
        newGroups.add(key);
      }
      patchState(store, { expandedGroups: newGroups });
    },

    expandNavGroup: (key: string) => {
      patchState(store, { expandedGroups: new Set([key]) });
    },

    collapseAllNavGroups: () =>
      patchState(store, { expandedGroups: new Set() }),

    isGroupExpanded: (key: string): boolean => store.expandedGroups().has(key),
  })),
);
